export interface RadarChartScores {
  strategie: number;
  marque: number;
  contenu: number;
  ia: number;
}

export interface RadarChartAxis {
  name: string;
  shortName: string;
  angle: number;
}

export interface RadarChartAxisState extends RadarChartAxis {
  score: number;
  visible: boolean;
}

export interface RadarChartPoint {
  x: number;
  y: number;
}

import { getLocale } from "../i18n/utils";

export type RadarAxisLabels = readonly [string, string, string, string];

export function resolveRadarAxisLabels(
  lang: string | null | undefined,
): RadarAxisLabels {
  const locale = lang === "fr" || lang === "nl" ? lang : "en";
  return getLocale(locale).radarAxisLabels;
}

export const RADAR_CHART = {
  width: 500,
  height: 400,
  center: {
    x: 250,
    y: 200,
  },
  radius: 120,
  maxScore: 4,
  gridLevels: [1, 2, 3, 4] as const,
  axes: [
    {
      name: "Stratégie\n& Pilotage",
      shortName: "Stratégie & Pilotage",
      angle: -90,
    },
    {
      name: "Marque\n& Expérience",
      shortName: "Marque & Expérience",
      angle: 0,
    },
    {
      name: "Contenu\n& Visibilité",
      shortName: "Contenu & Visibilité",
      angle: 90,
    },
    {
      name: "IA\n& Outils",
      shortName: "IA & Outils",
      angle: 180,
    },
  ] as const satisfies readonly RadarChartAxis[],
} as const;

function clampScore(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }

  return Math.max(0, Math.min(RADAR_CHART.maxScore, value));
}

function clampCompletedThemes(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }

  return Math.max(0, Math.min(RADAR_CHART.axes.length, Math.floor(value)));
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function getRadarChartPoint(
  angle: number,
  value: number,
): RadarChartPoint {
  const radians = (angle * Math.PI) / 180;
  const distance = (value / RADAR_CHART.maxScore) * RADAR_CHART.radius;

  return {
    x: RADAR_CHART.center.x + distance * Math.cos(radians),
    y: RADAR_CHART.center.y + distance * Math.sin(radians),
  };
}

export function buildRadarChartPath(points: RadarChartPoint[]): string {
  if (points.length === 0) {
    return "";
  }

  const path = `M ${points.map((point) => `${point.x},${point.y}`).join(" L ")}`;
  return points.length > 2 ? `${path} Z` : path;
}

// Wraps "X & Y" labels onto 2 lines at the separator, matching the built-in
// RADAR_CHART.axes[].name convention, so long translated labels (e.g. "Marque &
// Expérience") don't overflow the chart when rendered next to an axis point.
function toMultilineAxisLabel(label: string): string {
  const separatorIndex = label.indexOf(" & ");
  if (separatorIndex === -1) {
    return label;
  }

  return `${label.slice(0, separatorIndex)}\n& ${label.slice(separatorIndex + 3)}`;
}

export function buildRadarChartAxes(
  scores: RadarChartScores,
  completedThemes: number = RADAR_CHART.axes.length,
  axisLabels?: readonly [string, string, string, string],
): RadarChartAxisState[] {
  const visibleThemes = clampCompletedThemes(completedThemes);

  return RADAR_CHART.axes.map((axis, index) => ({
    ...axis,
    ...(axisLabels
      ? {
          name: toMultilineAxisLabel(axisLabels[index]),
          shortName: axisLabels[index],
        }
      : {}),
    score: clampScore(
      index === 0
        ? scores.strategie
        : index === 1
          ? scores.marque
          : index === 2
            ? scores.contenu
            : scores.ia,
    ),
    visible: visibleThemes >= index + 1,
  }));
}

function buildAxisLabelMarkup(axis: RadarChartAxisState): string {
  const labelDistance = RADAR_CHART.radius + 45;
  const baseX =
    RADAR_CHART.center.x +
    labelDistance * Math.cos((axis.angle * Math.PI) / 180);
  const baseY =
    RADAR_CHART.center.y +
    labelDistance * Math.sin((axis.angle * Math.PI) / 180);
  const xOffset = axis.angle === 0 ? 15 : 0;
  const yOffset = axis.angle === 90 ? -15 : 0;
  const labelX = baseX + xOffset;
  const labelY = baseY + yOffset;

  const scoreDistance =
    RADAR_CHART.radius * (axis.score / RADAR_CHART.maxScore) - 15;
  const scorePoint = {
    x:
      RADAR_CHART.center.x +
      scoreDistance * Math.cos((axis.angle * Math.PI) / 180),
    y:
      RADAR_CHART.center.y +
      scoreDistance * Math.sin((axis.angle * Math.PI) / 180),
  };

  return `
    <g>
      <text x="${labelX}" y="${labelY}" text-anchor="middle" font-family="Roboto, Arial, sans-serif" font-size="16" font-weight="700" fill="#ffffff" style="dominant-baseline: middle; opacity: 0.8;">
        ${axis.name
          .split("\n")
          .map(
            (line, lineIndex) =>
              `<tspan x="${labelX}" dy="${lineIndex === 0 ? 0 : 18}">${escapeHtml(line)}</tspan>`,
          )
          .join("")}
      </text>
      ${
        axis.visible && axis.score > 0
          ? `<text x="${axis.angle === -90 || axis.angle === 90 ? scorePoint.x : scorePoint.x + (axis.angle === 0 ? 45 : -45)}" y="${axis.angle === -90 ? scorePoint.y - 30 : axis.angle === 90 ? scorePoint.y + 35 : scorePoint.y + 5}" text-anchor="middle" font-family="Roboto, Arial, sans-serif" font-size="16" font-weight="700" fill="#ffffff">${axis.score.toFixed(1)}</text>`
          : ""
      }
    </g>`;
}

export function buildRadarChartSvgMarkup(
  scores: RadarChartScores,
  completedThemes: number = RADAR_CHART.axes.length,
  includeBackground: boolean = false,
  axisLabels?: readonly [string, string, string, string],
  svgTitle?: string,
  svgDesc?: string,
): string {
  const axes = buildRadarChartAxes(scores, completedThemes, axisLabels);
  const visibleAxes = axes.filter((axis) => axis.visible);
  const visiblePoints = visibleAxes.map((axis) =>
    getRadarChartPoint(axis.angle, axis.score),
  );
  const dataPath = buildRadarChartPath(visiblePoints);
  const title = svgTitle ?? "Radar chart";
  const desc = svgDesc ?? "";

  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${RADAR_CHART.width} ${RADAR_CHART.height}" preserveAspectRatio="xMidYMid meet" style="width: 100%; height: auto; display: block;" role="img" aria-labelledby="radar-chart-title radar-chart-desc">
  <title id="radar-chart-title">${title}</title>
  <desc id="radar-chart-desc">${desc}</desc>
  <defs>
    ${
      includeBackground
        ? `<linearGradient id="background" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="rgb(2, 50, 74)" />
      <stop offset="100%" stop-color="rgb(0, 175, 253)" />
    </linearGradient>`
        : ""
    }
    <linearGradient id="fill" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#ff00cd" stop-opacity="0.2" />
      <stop offset="100%" stop-color="#32e6ff" stop-opacity="0.1" />
    </linearGradient>
  </defs>

  ${includeBackground ? `<rect x="0" y="0" width="${RADAR_CHART.width}" height="${RADAR_CHART.height}" fill="url(#background)" />` : ""}

  ${RADAR_CHART.gridLevels
    .map(
      (level) => `
    <circle
      cx="${RADAR_CHART.center.x}"
      cy="${RADAR_CHART.center.y}"
      r="${(level / RADAR_CHART.maxScore) * RADAR_CHART.radius}"
      fill="none"
      stroke="rgba(255, 255, 255, 0.3)"
      stroke-width="1"
    />`,
    )
    .join("")}

  ${RADAR_CHART.axes
    .map((axis) => {
      const endPoint = getRadarChartPoint(axis.angle, RADAR_CHART.maxScore);
      return `<line x1="${RADAR_CHART.center.x}" y1="${RADAR_CHART.center.y}" x2="${endPoint.x}" y2="${endPoint.y}" stroke="rgba(255, 255, 255, 0.2)" stroke-width="1" />`;
    })
    .join("")}

  ${dataPath ? `<path d="${dataPath}" fill="url(#fill)" stroke="none" />` : ""}
  ${dataPath ? `<path d="${dataPath}" fill="none" stroke="#FF00D2" stroke-width="2" />` : ""}

  ${visibleAxes
    .map((axis) => {
      const point = getRadarChartPoint(axis.angle, axis.score);
      return `<circle cx="${point.x}" cy="${point.y}" r="6" fill="#FF00D2" stroke="white" stroke-width="2" />`;
    })
    .join("")}

  ${axes.map((axis) => buildAxisLabelMarkup(axis)).join("")}

  ${RADAR_CHART.gridLevels
    .map(
      (level) => `
    <text x="${RADAR_CHART.center.x + (level / RADAR_CHART.maxScore) * RADAR_CHART.radius + 10}" y="${RADAR_CHART.center.y - 5}" font-family="Roboto, Arial, sans-serif" font-size="11" fill="rgba(255, 255, 255, 0.3)">${level}</text>`,
    )
    .join("")}
</svg>`;
}
